import { useDropzone, FileError } from 'react-dropzone';
import { Button, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { MdClose, MdCheck } from "react-icons/md";
import { FiImage, FiLoader, FiPlus, FiUploadCloud } from "react-icons/fi";
import Resizer from 'react-image-file-resizer';
import ns from '@/helpers/NotificationService';
import HeaderText from '../HeaderText/HeaderText';

interface Props {
    newFilesAdded: (images: Image[]) => void;
    deleteImage: (id: string) => void;
    lotImages: any;
    isDisplayImage: boolean;
    header: string;
    description: string;
    min?: number;
    max?: number;
    onValidate?: (valid: boolean) => void;
    acceptedFileExtensions: string;
    // The saved images are still being fetched: show placeholders instead of the "add" prompt.
    loading?: boolean;
}

export interface Image {
    id: string;
    uploadedFile: File | undefined;
    imageUrl: string;
    isDisplayImage: boolean;
    orderInt: number;
}

const ImageUploader: React.FC<Props> = props => {
    const {
        deleteImage,
        newFilesAdded,
        onValidate,
        lotImages,
        isDisplayImage,
        header,
        description,
        min = 0,
        max = 50,
        acceptedFileExtensions,
        loading = false
    } = props;
    const [uploadedFiles, setUploadedFiles] = React.useState([] as Image[]);
    const [isBusy, setIsBusy] = React.useState(false);

    const imageValidator = (file: any): any => {
        if (!acceptedFileExtensions.includes(file.type)) {
            setIsBusy(false);
            // ns.error(`Image ${file.name} is in the incorrect format. We only support .jpeg, .jpg and .png file types`);
            return {
                code: "incorrect-format",
                message: "Supported file types are .JPEG | .JPG | .PNG"
            };
        }

        /* Added code */
        if (file.size > 10485760) {
            setIsBusy(false);
            ns.error(`Image ${file.name} is too big. A maximum image size of 10MB is allowed`);
            return {
                code: "size-too-large",
                message: "Size is larger than 10mb"
            };
        }

        return null;
    }

    const {
        acceptedFiles,
        fileRejections,
        isDragActive,
        isDragAccept,
        isDragReject, getRootProps, getInputProps, open
    } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'text/html': ['.html', '.htm'],
            'image/jpeg': ['.jpeg', '.jpg']
        },
        multiple: true,
        validator: imageValidator
    });

    React.useEffect(() => {
        let countProcessing = acceptedFiles?.length ?? 0;
        let countUploaded = 0;
        if (isDisplayImage) {
            countUploaded = uploadedFiles.filter(file => file.isDisplayImage).length;
        } else {
            countUploaded = uploadedFiles.filter(file => !file.isDisplayImage).length;
        }

        if ((min === 1 && max === 1 && countProcessing === 1) || max >= (countUploaded + countProcessing)) {
            const imageUrls: Image[] = [];
            const resizedImages: any[] = acceptedFiles.map((file, i) => resizeFile(file, i,
                uploadedFiles?.length > 0 ? uploadedFiles[uploadedFiles.length - 1].orderInt + 1 : 0, isDisplayImage));

            Promise.allSettled(resizedImages).then((responses) => {
                (responses as unknown as any[]).forEach((resizeResult) => {
                    imageUrls.push(resizeResult.value);
                    setIsBusy(false);
                });

                if (min === 1 && max === 1 && imageUrls.length === 1 && countUploaded === 1) {
                    let fileToDelete: any = undefined;
                    if (isDisplayImage) {
                        fileToDelete = uploadedFiles.filter(file => file.isDisplayImage)[0];
                    } else {
                        fileToDelete = uploadedFiles.filter(file => !file.isDisplayImage)[0];
                    }
                    if ((fileToDelete?.id ?? "") !== "" && deleteImage) deleteImage(fileToDelete.id);
                    setUploadedFiles(imageUrls);
                }
                else if (imageUrls.length > 0)
                    setUploadedFiles(uploadedFiles.concat(imageUrls));
            });
        } else {
            ns.error("The maximum amount of images allowed has been reached");
            setIsBusy(false);
        }
    }, [acceptedFiles]);

    React.useEffect(() => {
        if (lotImages) {
            let imageUrls: Image[] = lotImages.map(image => {
                return {
                    id: image.id,
                    uploadedFile: undefined,
                    imageUrl: image.imageUrl,
                    isDisplayImage: image.isDisplayImage,
                    orderInt: image.orderInt
                } as Image
            });

            setUploadedFiles(imageUrls);
        } else {
            setUploadedFiles([]);
        }
    }, [lotImages]);

    React.useEffect(() => {
        if (uploadedFiles) newFilesAdded(uploadedFiles);

        validate(uploadedFiles);
    }, [uploadedFiles]);

    const validate = (uploadedFiles) => {
        let valid = false;

        if (uploadedFiles?.length >= min)
            valid = true;

        if (onValidate) {
            onValidate(valid);
        }
    }

    useEffect(() => {
        if (fileRejections?.length === 1) ns.error(`Image ${fileRejections?.[0]?.file.name} is in the incorrect format. We only support .jpeg, .jpg and .png file types`);
        else if (fileRejections?.length > 1) ns.error(`Some images are in the incorrect format. We only support .jpeg, .jpg and .png file types`);
    }, [fileRejections])

    const resizeFile = (file: File, orderInt: number, offset: number, isDisplayImage: boolean) => new Promise(resolve => {
        if (!isBusy) setIsBusy(true);
        Resizer.imageFileResizer(file, 1920, 1080, 'JPEG', 50, 0,
            base64 => {
                const file = {
                    id: "",
                    uploadedFile: undefined,
                    imageUrl: base64,
                    isDisplayImage: isDisplayImage,
                    orderInt: offset + orderInt
                } as Image
                resolve(file);
            }, 'base64', undefined, undefined
        );
    });

    const remove = (file, index, list) => {
        if (min > 0 && list?.length === min) return;

        if (file && file.id && file.id !== "") {
            if (deleteImage)
                deleteImage(file.id);
        }

        const currentIndex = uploadedFiles.indexOf(file);
        const newState = uploadedFiles.slice();
        newState.splice(currentIndex, 1);
        setUploadedFiles(newState);
    }

    // Images of this uploader (display image or gallery), in their order.
    const shownFiles = uploadedFiles
        .filter(file => isDisplayImage === true ? file.isDisplayImage : !file.isDisplayImage)
        .sort((image1, image2) => {
            if (image1.orderInt > image2.orderInt)
                return 1;
            if (image1.orderInt < image2.orderInt)
                return -1;
            return 0;
        });
    const single = isDisplayImage !== false;
    const showPlaceholders = loading && shownFiles.length === 0;

    const dropzoneClass = (isDragAccept ? " is-accept" : "") + (isDragReject ? " is-reject" : "");
    const rejectedList = fileRejections?.length > 0 && <div className="pb-dropzone-rejected">
        <span>Images with incorrect format: </span>
        <span className="text-danger">{fileRejections.map(x => x.file.name).filter(x => x).join(", ")}</span>
    </div>;

    // Several images: a photo grid. The "Add photos" drop tile comes first (the whole width while there are none),
    // then each photo with its position and a remove button.
    if (!single) {
        return (
            <div className="pb-uploader pb-uploader--grid">
                <div className="pb-uploader-head">
                    <HeaderText header={header} description={description} />
                    <span className={"pb-uploader-counter" + (shownFiles.length < min ? " is-short" : "")}>
                        {shownFiles.length} / {max} photos
                    </span>
                </div>

                <div className={"pb-photo-grid" + (shownFiles.length === 0 ? " is-empty" : "")}>
                    {showPlaceholders && <>
                        <div className="pb-photo-skeleton pb-photo-skeleton--wide" />
                        <div className="pb-photo-skeleton" />
                        <div className="pb-photo-skeleton" />
                    </>}

                    {!showPlaceholders && <div {...getRootProps({ className: "pb-photo-add" + dropzoneClass })}>
                        <input {...getInputProps()} />
                        <div className="pb-photo-add-icon">
                            {isDragAccept ? <MdCheck /> : isDragReject ? <MdClose /> : isBusy ? <FiLoader className="pb-spin" /> : <FiPlus />}
                        </div>
                        <div className="pb-photo-add-body">
                            <div className="pb-photo-add-title">
                                {isDragAccept ? "Drop to add" : isDragReject ? "Incorrect file type" : isBusy ? "Processing..." : "Add photos"}
                            </div>
                            {isDragReject
                                ? <div className="pb-photo-add-text">A max of 50 JPEG / JPG / PNG images no larger than 10mb are accepted.</div>
                                : <>
                                    <div className="pb-photo-add-text">Drag and drop or click to browse</div>
                                    <ul className="pb-banner-tips pb-photo-add-tips">
                                        <li><strong>JPEG / JPG / PNG</strong>, up to 10MB each</li>
                                        <li>Up to <strong>{max} photos</strong></li>
                                        <li><strong>16x9</strong> works best; other sizes may be cut to fit</li>
                                    </ul>
                                </>}
                        </div>
                    </div>}

                    {shownFiles.map((file, index, list) => (
                        <div className="pb-photo" key={index}>
                            <img src={file.imageUrl} alt="" />
                            <span className="pb-photo-order">{index + 1}</span>
                            {list?.length > min && <Tooltip arrow title="Remove" placement="bottom">
                                <button type="button" className="pb-photo-remove" aria-label="Remove image" onClick={() => remove(file, index, list)}>
                                    <MdClose />
                                </button>
                            </Tooltip>}
                        </div>
                    ))}
                </div>

                {rejectedList}
            </div>
        );
    }

    // A single image (auction image, lot banner): the frame is the drop area and shows the image (click or drop to
    // replace, remove in the corner); tips and an upload / replace button beside it. The frame keeps its size whether
    // the image is there, still loading or not added yet.
    const current = shownFiles[0];
    return (
        <div className="pb-uploader">
            <HeaderText header={header} description={description} />
            <div className="pb-banner">
                {showPlaceholders && <div className="pb-banner-frame pb-banner-frame--loading" />}

                {!showPlaceholders && <div {...getRootProps({ className: "pb-banner-frame" + dropzoneClass + (current ? " has-image" : "") })}>
                    <input {...getInputProps()} />
                    {current && <img src={current.imageUrl} alt="" />}

                    {!current && !isDragActive && <div className="pb-banner-empty">
                        <div className="pb-photo-add-icon">{isBusy ? <FiLoader className="pb-spin" /> : <FiImage />}</div>
                        <div className="pb-photo-add-title">{isBusy ? "Processing..." : "Add an image"}</div>
                        {!isBusy && <div className="pb-photo-add-text">Drag and drop or click to browse</div>}
                    </div>}

                    {current && !isDragActive && <div className="pb-banner-hover">
                        {isBusy ? <><FiLoader className="pb-spin" /> Processing...</> : <><FiUploadCloud /> Click or drop to replace</>}
                    </div>}

                    {isDragActive && <div className="pb-banner-drop">
                        {isDragReject ? <><MdClose /> Incorrect file type. A max of 1 JPEG / JPG / PNG image no larger than 10mb is accepted.</>
                            : <><MdCheck /> {current ? "Drop to replace" : "Drop to add"}</>}
                    </div>}

                    {uploadedFiles.length < min && <span className="pb-dropzone-required">Required</span>}

                    {current && shownFiles.length > min && <Tooltip arrow title="Remove" placement="bottom">
                        <button type="button" className="pb-photo-remove" aria-label="Remove image" onClick={(e) => { e.stopPropagation(); remove(current, 0, shownFiles); }}>
                            <MdClose />
                        </button>
                    </Tooltip>}
                </div>}

                <div className="pb-banner-info">
                    <div className="pb-banner-info-title">Image tips</div>
                    <ul className="pb-banner-tips">
                        <li><strong>16x9</strong> works best, e.g. 1920 x 1080 or 1280 x 720</li>
                        <li><strong>JPEG / JPG / PNG</strong>, up to 10MB</li>
                        <li>Other sizes may be cut to fit</li>
                    </ul>
                    <Button disabled={isBusy || showPlaceholders} onClick={open} className="btn-primary text-white text-capitalize pb-dropzone-button">
                        {isBusy ? "Processing..." : current ? "Replace Image" : "Upload Image"}
                    </Button>
                    {rejectedList}
                </div>
            </div>
        </div>
    );
}

export default ImageUploader